import React, { useState, useEffect, useRef } from 'react';
import { log, logError, logWarn, downloadLogs, flushLogs } from './logger';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Calculate absorption power using the formula: airflow × power / efficiency
 * 
 * @param {number} airflow - Volume flow rate in m³/h (qv value)
 * @param {number} power - Electrical input power in watts (W)
 * @param {number} efficiency - Fan efficiency (0-1 for decimal, or 0-100 for percentage)
 * @param {boolean} efficiencyIsPercentage - If true, efficiency is treated as percentage (0-100), otherwise as decimal (0-1)
 * @returns {number} Absorption power in watts (W)
 */
// eslint-disable-next-line no-unused-vars
function calculateAbsorptionPower(airflow, power, efficiency, efficiencyIsPercentage = false) {
  if (!airflow || !power || !efficiency || efficiency === 0) {
    return 0;
  }
  
  const efficiencyDecimal = efficiencyIsPercentage ? efficiency / 100 : efficiency;
  const absorptionPower = (airflow * power) / efficiencyDecimal;
  
  return absorptionPower;
}

function FanGraphChartJS() {
  const chartRef = useRef(null);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operatingPoint, setOperatingPoint] = useState({ qv: 2335.46, psf: 762.77 });
  const [showAdditionalCurves, setShowAdditionalCurves] = useState({
    current: false,
    fanPressure: false,
    power: false,
    staticEfficiency: false,
    totalEfficiency: false,
    speed: false
  });
  
  // FIXED DOMAIN: Calculate once when data loads, never change
  const [fixedDomain, setFixedDomain] = useState(null);
  const clickHistoryRef = useRef([]);
  
  // Motor options state
  const [motorOptions, setMotorOptions] = useState([]);
  const [selectedMotor, setSelectedMotor] = useState(null);
  
  // Catalog specifications state
  const [catalogSpecs, setCatalogSpecs] = useState(null);

  // Log operating point changes
  useEffect(() => {
    log('FanGraphChartJS', 'Operating point changed', operatingPoint);
  }, [operatingPoint]);

  // Log selected motor changes and force chart update (without animation)
  useEffect(() => {
    log('FanGraphChartJS', 'Selected motor changed', {
      selectedMotor: selectedMotor ? {
        power: selectedMotor.power_rating_kw,
        rpm: selectedMotor.fan_rpm
      } : null
    });
    
    // Force chart update if chart exists (without animation)
    if (chartRef.current) {
      const chart = chartRef.current;
      chart.update('none'); // Update without animation
      log('FanGraphChartJS', 'Chart updated after motor selection change');
    }
  }, [selectedMotor]);

  // Add direct canvas click handler as fallback (runs after chart is rendered)
  useEffect(() => {
    if (!graphData) return; // Wait for data to load
    
    let cleanup = null;
    
    // Wait for chart to be rendered
    const timeout = setTimeout(() => {
      if (chartRef.current && chartRef.current.canvas) {
        const canvas = chartRef.current.canvas;
        console.log('Setting up canvas click handler', canvas);
        
        const handleCanvasClick = (e) => {
          console.log('CANVAS CLICKED DIRECTLY!', e);
          handleChartClick(e, []);
        };
        
        canvas.addEventListener('click', handleCanvasClick);
        
        // Store cleanup function
        cleanup = () => {
          canvas.removeEventListener('click', handleCanvasClick);
        };
      }
    }, 500); // Give chart time to render
    
    return () => {
      clearTimeout(timeout);
      if (cleanup) cleanup();
    };
  }, [graphData]);

  useEffect(() => {
    log('FanGraphChartJS', 'Component mounted, loading data');
    setLoading(true);
    setError(null);
    
    Promise.all([
      fetch(`${process.env.PUBLIC_URL || ''}/graph_data.json`).then(r => {
        if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
        return r.json();
      }),
      fetch(`${process.env.PUBLIC_URL || ''}/motor_options_data.json`).then(r => {
        if (!r.ok) {
          logWarn('FanGraphChartJS', 'Motor options file not found, continuing without motor selection');
          return null;
        }
        return r.json();
      }),
      fetch(`${process.env.PUBLIC_URL || ''}/fan_catalog_specs.json`).then(r => {
        if (!r.ok) {
          logWarn('FanGraphChartJS', 'Catalog specs file not found, continuing without catalog info');
          return null;
        }
        return r.json();
      })
    ])
      .then(([graphData, motorData, catalogData]) => {
        log('FanGraphChartJS', 'Data loaded', {
          hasFanCurve: !!graphData.fan_curve,
          hasFanCurves: !!graphData.fan_curves,
          fanCurveLength: graphData.fan_curve?.length || 0,
          fanCurvesLength: graphData.fan_curves?.length || 0,
          operatingPoint: graphData.operating_point,
          motorOptionsCount: motorData?.motor_options?.length || 0
        });
        
        if (!graphData || (!graphData.fan_curve && !graphData.fan_curves)) {
          throw new Error('Invalid data structure: missing fan curve data');
        }
        setGraphData(graphData);
        
        if (motorData && motorData.motor_options) {
          setMotorOptions(motorData.motor_options);
          log('FanGraphChartJS', 'Motor options loaded', { count: motorData.motor_options.length });
        }
        
        if (catalogData) {
          setCatalogSpecs(catalogData);
          log('FanGraphChartJS', 'Catalog specs loaded', { fanModel: catalogData.fan_model });
        }
        
        // Calculate FIXED domain once when data loads
        const fanCurve = graphData.fan_curve || (graphData.fan_curves && graphData.fan_curves[0]?.points) || [];
        if (fanCurve.length > 0) {
          const fanCurveMaxQv = Math.max(...fanCurve.map(d => d.qv), 0);
          const fanCurveMaxPsf = Math.max(...fanCurve.map(d => d.psf), 0);
          
          const fixedMaxQv = Math.ceil(fanCurveMaxQv * 1.1);
          const fixedMaxPsf = Math.ceil(fanCurveMaxPsf * 1.1);
          
          setFixedDomain({
            minQv: 0,
            maxQv: fixedMaxQv,
            minPsf: 0,
            maxPsf: fixedMaxPsf
          });
          
          log('FanGraphChartJS', 'Fixed domain calculated', { 
            fixedMaxQv, 
            fixedMaxPsf,
            fanCurveMaxQv,
            fanCurveMaxPsf,
            note: 'Domain is fixed and will never change'
          });
        }
        
        if (graphData.operating_point && graphData.operating_point.qv) {
          log('FanGraphChartJS', 'Setting initial operating point', graphData.operating_point);
          setOperatingPoint(graphData.operating_point);
        }
        setLoading(false);
        log('FanGraphChartJS', 'Data loading complete');
      })
      .catch(error => {
        logError('FanGraphChartJS', 'Error loading graph data', { error: error.message, stack: error.stack });
        setError(error.message || 'Failed to load graph data');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading graph data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <div>Error: {error}</div>
        <div style={{ marginTop: '10px', fontSize: '14px' }}>
          Please ensure graph_data.json exists in the public folder.
        </div>
      </div>
    );
  }

  if (!graphData) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>No graph data available</div>
      </div>
    );
  }

  // Prepare fan curve data
  let fanCurveData = [];
  if (graphData.fan_curve && Array.isArray(graphData.fan_curve)) {
    const fanCurveMap = new Map();
    graphData.fan_curve.forEach(point => {
      if (point && point.qv !== undefined && point.psf !== undefined) {
        const key = Math.round(point.qv);
        if (!fanCurveMap.has(key) || fanCurveMap.get(key).psf < point.psf) {
          fanCurveMap.set(key, { qv: point.qv, psf: point.psf });
        }
      }
    });
    fanCurveData = Array.from(fanCurveMap.values()).sort((a, b) => a.qv - b.qv);
  } else if (graphData.fan_curves && Array.isArray(graphData.fan_curves)) {
    // Use the first fan curve (primary curve)
    const primaryCurve = graphData.fan_curves[0];
    if (primaryCurve && primaryCurve.points) {
      fanCurveData = primaryCurve.points.map(p => ({ qv: p.qv, psf: p.psf })).sort((a, b) => a.qv - b.qv);
    }
  }

  // Prepare system resistance data
  // If not in JSON, calculate from operating point using psf = k * qv^2
  let systemResistanceData = [];
  if (graphData.system_resistance && Array.isArray(graphData.system_resistance) && graphData.system_resistance.length > 0) {
    systemResistanceData = graphData.system_resistance.map(p => ({ qv: p.qv, psf: p.psf })).sort((a, b) => a.qv - b.qv);
  } else if (operatingPoint.qv > 0 && operatingPoint.psf > 0) {
    // Calculate system resistance curve: psf = k * qv^2
    // k = psf / (qv^2) at operating point
    const k = operatingPoint.psf / (operatingPoint.qv * operatingPoint.qv);
    const maxQv = fixedDomain ? fixedDomain.maxQv : 10000;
    const stepSize = 200;
    
    for (let qv = 0; qv <= maxQv; qv += stepSize) {
      const psf = k * qv * qv;
      systemResistanceData.push({ qv, psf });
    }
  }

  // Prepare ErP range data
  const erpRangeData = graphData.erp_range && Array.isArray(graphData.erp_range)
    ? graphData.erp_range.map(p => ({ qv: p.qv, psf: p.psf })).sort((a, b) => a.qv - b.qv)
    : [];

  // Get domain values
  const maxQv = fixedDomain ? fixedDomain.maxQv : 10000;
  const maxPsf = fixedDomain ? fixedDomain.maxPsf : 1000;
  const minQv = fixedDomain ? fixedDomain.minQv : 0;
  const minPsf = fixedDomain ? fixedDomain.minPsf : 0;

  // Find closest motor above clicked point
  const findClosestMotorAbove = (qv, psf) => {
    if (!motorOptions || motorOptions.length === 0) {
      return null;
    }
    
    const motorCandidates = [];
    const allMotors = [];
    
    for (const motor of motorOptions) {
      const curvePoints = motor.curve_points || [];
      if (curvePoints.length === 0) continue;
      
      const minQv = Math.min(...curvePoints.map(p => p.qv));
      const maxQv = Math.max(...curvePoints.map(p => p.qv));
      let pressureAtQv = null;
      
      if (qv < minQv) {
        pressureAtQv = curvePoints[0].psf;
      } else if (qv > maxQv) {
        pressureAtQv = curvePoints[curvePoints.length - 1].psf;
      } else {
        let pointBefore = null;
        let pointAfter = null;
        
        for (let i = 0; i < curvePoints.length - 1; i++) {
          if (curvePoints[i].qv <= qv && curvePoints[i + 1].qv >= qv) {
            pointBefore = curvePoints[i];
            pointAfter = curvePoints[i + 1];
            break;
          }
        }
        
        const exactMatch = curvePoints.find(p => Math.abs(p.qv - qv) < 0.1);
        if (exactMatch) {
          pressureAtQv = exactMatch.psf;
        } else if (pointBefore && pointAfter) {
          const ratio = (qv - pointBefore.qv) / (pointAfter.qv - pointBefore.qv);
          pressureAtQv = pointBefore.psf + ratio * (pointAfter.psf - pointBefore.psf);
        }
      }
      
      if (pressureAtQv !== null) {
        const pressureDifference = pressureAtQv - psf;
        
        allMotors.push({
          motor,
          pressureAtQv,
          difference: pressureDifference,
          distance: Math.abs(pressureDifference)
        });
        
        if (pressureDifference >= 0) {
          motorCandidates.push({
            motor,
            pressureAtQv,
            difference: pressureDifference,
            distance: Math.abs(pressureDifference)
          });
        }
      }
    }
    
    if (motorCandidates.length > 0) {
      motorCandidates.sort((a, b) => {
        if (a.motor.power_rating_kw !== b.motor.power_rating_kw) {
          return a.motor.power_rating_kw - b.motor.power_rating_kw;
        }
        return a.distance - b.distance;
      });
      
      return motorCandidates[0].motor;
    }
    
    if (allMotors.length > 0) {
      allMotors.sort((a, b) => {
        if (Math.abs(a.distance - b.distance) > 1) {
          return a.distance - b.distance;
        }
        return a.motor.power_rating_kw - b.motor.power_rating_kw;
      });
      
      return allMotors[0].motor;
    }
    
    return null;
  };

  // Handle click on chart using Chart.js built-in coordinate conversion
  const handleChartClick = (event, elements) => {
    console.log('CHART CLICKED!', { event, elements, hasChartRef: !!chartRef.current });
    
    if (!chartRef.current) {
      logWarn('ClickHandler', 'Chart ref not available');
      return;
    }

    const chart = chartRef.current;
    
    // Chart.js onClick provides the native event - handle both React synthetic and native events
    let nativeEvent;
    if (event.nativeEvent) {
      nativeEvent = event.nativeEvent;
    } else if (event.clientX !== undefined) {
      nativeEvent = event;
    } else {
      logWarn('ClickHandler', 'Could not extract native event from', event);
      return;
    }
    
    // Get the canvas element from the chart
    const canvas = chart.canvas;
    if (!canvas) {
      logWarn('ClickHandler', 'Canvas not found');
      return;
    }
    
    const rect = canvas.getBoundingClientRect();
    
    // Calculate click position relative to canvas
    const canvasPosition = {
      x: nativeEvent.clientX - rect.left,
      y: nativeEvent.clientY - rect.top
    };

    console.log('Canvas position calculated', { 
      canvasPosition, 
      rect, 
      clientX: nativeEvent.clientX, 
      clientY: nativeEvent.clientY,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height
    });

    // Use Chart.js built-in methods to convert pixel to data coordinates
    const dataX = chart.scales.x.getValueForPixel(canvasPosition.x);
    const dataY = chart.scales.y.getValueForPixel(canvasPosition.y);
    
    console.log('Data coordinates calculated', { dataX, dataY, canvasPosition });

    log('ClickHandler', 'Chart.js coordinate conversion', {
      canvasX: canvasPosition.x,
      canvasY: canvasPosition.y,
      dataX: dataX.toFixed(2),
      dataY: dataY.toFixed(2),
      algorithm: 'Chart.js getValueForPixel (built-in)'
    });

    // Record click in history
    const clickRecord = {
      timestamp: new Date().toISOString(),
      pixelX: canvasPosition.x,
      pixelY: canvasPosition.y,
      algorithm: 'Chart.js getValueForPixel',
      calculatedData: { qv: parseFloat(dataX.toFixed(2)), psf: parseFloat(dataY.toFixed(2)) }
    };
    
    clickHistoryRef.current.push(clickRecord);
    
    log('ClickHandler', '=== CLICK COORDINATE HISTORY TABLE ===', {
      totalClicks: clickHistoryRef.current.length,
      clickHistory: clickHistoryRef.current.map((record, idx) => ({
        '#': idx + 1,
        'Pixel (X,Y)': `(${record.pixelX.toFixed(1)}, ${record.pixelY.toFixed(1)})`,
        'Algorithm': record.algorithm,
        'Calculated Data (qv,psf)': `(${record.calculatedData.qv}, ${record.calculatedData.psf})`
      }))
    });

    // Clamp to valid ranges
    const clampedQv = Math.max(minQv, Math.min(maxQv, dataX));
    const clampedPsf = Math.max(minPsf, Math.min(maxPsf, dataY));

    log('ClickHandler', 'Setting operating point from click', {
      clickedQv: dataX.toFixed(2),
      clickedPsf: dataY.toFixed(2),
      clampedQv: clampedQv.toFixed(2),
      clampedPsf: clampedPsf.toFixed(2)
    });

    // Set operating point with the clicked values (this will update the text boxes)
    // Use the exact clicked values, not clamped (unless they're out of bounds)
    setOperatingPoint({
      qv: clampedQv,
      psf: clampedPsf
    });
    
    log('ClickHandler', 'Operating point state updated', {
      qv: clampedQv,
      psf: clampedPsf
    });

    // Find closest motor above this point
    const foundMotor = findClosestMotorAbove(clampedQv, clampedPsf);
    log('ClickHandler', 'Motor selection result', {
      foundMotor: foundMotor ? {
        power: foundMotor.power_rating_kw,
        rpm: foundMotor.fan_rpm,
        poles: foundMotor.poles
      } : null,
      clickedQv: clampedQv,
      clickedPsf: clampedPsf,
      motorOptionsCount: motorOptions.length
    });
    
    if (foundMotor) {
      setSelectedMotor(foundMotor);
      log('ClickHandler', 'Motor selected and state updated', {
        power: foundMotor.power_rating_kw,
        rpm: foundMotor.fan_rpm,
        poles: foundMotor.poles
      });
    } else {
      setSelectedMotor(null);
      logWarn('ClickHandler', 'No motor found for clicked point');
    }

    flushLogs();
  };

  // Prepare datasets for Chart.js
  const datasets = [];

  // ErP Range (filled area)
  if (erpRangeData.length > 0) {
    datasets.push({
      label: 'ErP konformer Bereich',
      data: erpRangeData.map(p => ({ x: p.qv, y: p.psf })),
      borderColor: 'transparent',
      backgroundColor: 'rgba(135, 206, 235, 0.3)',
      fill: true,
      pointRadius: 0,
      borderWidth: 0,
      order: 0
    });
  }

  // Main Fan Curve - Darker gray dashed lines
  if (fanCurveData.length > 0) {
    datasets.push({
      label: 'Fan Curve',
      data: fanCurveData.map(p => ({ x: p.qv, y: p.psf })),
      borderColor: 'rgba(85, 85, 85, 0.8)', // Darker gray, 80% opacity
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [4, 4],
      pointRadius: 0,
      fill: false,
      order: 1
    });
  }

  // System Resistance Curve - Purple
  if (systemResistanceData.length > 0) {
    datasets.push({
      label: 'System Resistance',
      data: systemResistanceData.map(p => ({ x: p.qv, y: p.psf })),
      borderColor: '#9370db',
      backgroundColor: 'transparent',
      borderWidth: 3,
      borderDash: [5, 5],
      pointRadius: 0,
      fill: false,
      order: 2
    });
  }

  // Motor Option Curves - Highlight when selected
  log('DatasetCreation', 'Creating motor curves', {
    motorOptionsCount: motorOptions.length,
    selectedMotor: selectedMotor ? {
      power: selectedMotor.power_rating_kw,
      rpm: selectedMotor.fan_rpm
    } : null
  });
  
  motorOptions.forEach((motor) => {
    const isSelected = selectedMotor && selectedMotor.power_rating_kw === motor.power_rating_kw;
    const curvePoints = motor.curve_points || [];
    if (curvePoints.length > 0) {
      log('DatasetCreation', `Motor ${motor.power_rating_kw}kW`, {
        isSelected,
        curvePointsCount: curvePoints.length,
        borderColor: isSelected ? '#ff6600' : 'rgba(136, 132, 216, 0.6)',
        borderWidth: isSelected ? 3 : 1
      });
      datasets.push({
        label: `${motor.power_rating_kw}kW (${motor.fan_rpm.toFixed(0)} RPM)`,
        data: curvePoints.map(p => ({ x: p.qv, y: p.psf })),
        borderColor: isSelected ? '#ff6600' : 'rgba(136, 132, 216, 0.6)', // Orange when selected, purple 60% opacity when not
        backgroundColor: 'transparent',
        borderWidth: isSelected ? 3 : 1, // Bold (3px) when selected, thin (1px) when not
        borderDash: isSelected ? [] : [2, 2], // Solid when selected, dashed when not
        pointRadius: 0,
        fill: false,
        order: isSelected ? 4 : 3 // Selected motor on top
      });
    }
  });

  // Voltage Curves (if any) - purple dashed lines (matching Recharts)
  if (graphData.voltage_curves) {
    Object.keys(graphData.voltage_curves).forEach(voltage => {
      const voltageData = graphData.voltage_curves[voltage];
      if (voltageData && Array.isArray(voltageData) && voltageData.length > 0) {
        datasets.push({
          label: `${voltage}V`,
          data: voltageData.map(p => ({ x: p.qv, y: p.psf })),
          borderColor: '#8884d8', // Purple like Recharts
          backgroundColor: 'transparent',
          borderWidth: 1,
          borderDash: [2, 2], // Dashed like Recharts
          pointRadius: 0,
          fill: false,
          order: 2
        });
      }
    });
  }

  // Operating Point Vertical Reference Line - Light gray crosshair
  datasets.push({
    label: 'Operating Point (X)',
    data: [
      { x: operatingPoint.qv, y: minPsf },
      { x: operatingPoint.qv, y: maxPsf }
    ],
    borderColor: '#d0d0d0',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderDash: [3, 3],
    pointRadius: 0,
    fill: false,
    order: 9,
    pointHoverRadius: 0,
    pointHoverBorderWidth: 0
  });

  // Operating Point Horizontal Reference Line - Light gray crosshair
  datasets.push({
    label: 'Operating Point (Y)',
    data: [
      { x: minQv, y: operatingPoint.psf },
      { x: maxQv, y: operatingPoint.psf }
    ],
    borderColor: '#d0d0d0',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderDash: [3, 3],
    pointRadius: 0,
    fill: false,
    order: 9,
    pointHoverRadius: 0,
    pointHoverBorderWidth: 0
  });

  // Operating Point Marker
  datasets.push({
    label: 'Operating Point',
    data: [{ x: operatingPoint.qv, y: operatingPoint.psf }],
    borderColor: '#003366',
    backgroundColor: '#003366',
    borderWidth: 2,
    pointRadius: 8, // Keep the operating point marker visible
    pointHoverRadius: 10,
    pointBackgroundColor: '#003366',
    pointBorderColor: '#ffffff',
    pointBorderWidth: 2,
    showLine: false,
    order: 10
  });

  // Chart configuration
  const chartData = {
    datasets: datasets
  };
  
  // Log dataset creation for debugging
  log('DatasetCreation', 'Final datasets created', {
    totalDatasets: datasets.length,
    motorDatasets: datasets.filter(d => d.label && d.label.includes('kW')).length,
    selectedMotorInDatasets: datasets.some(d => d.borderColor === '#ff6600'),
    selectedMotorState: selectedMotor ? selectedMotor.power_rating_kw : null
  });
  
  // Log dataset creation for debugging
  log('DatasetCreation', 'Final datasets created', {
    totalDatasets: datasets.length,
    motorDatasets: datasets.filter(d => d.label.includes('kW')).length,
    selectedMotorInDatasets: datasets.some(d => d.borderColor === '#ff6600')
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false, // Disable all animations
    transitions: {
      active: {
        animation: {
          duration: 0 // No animation on state changes
        }
      }
    },
    elements: {
      point: {
        radius: 0, // Hide all points by default
        hoverRadius: 0
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top',
        display: true,
        filter: (legendItem) => {
          // Hide reference lines from legend
          return !legendItem.text.includes('Operating Point (X)') && 
                 !legendItem.text.includes('Operating Point (Y)');
        }
      },
      title: {
        display: false
      },
      tooltip: {
        enabled: false // Disable tooltip completely
      }
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Volumenstrom qv [m³/h]'
        },
        min: minQv,
        max: maxQv,
        grid: {
          color: '#fefefe', // Very light gray grid
          borderDash: [3, 3]
        },
        ticks: {
          stepSize: 200,
          maxTicksLimit: Math.ceil((maxQv - minQv) / 200),
          callback: function(value) {
            // Round to nearest 100 and return as whole number (no decimals)
            const rounded = Math.round(value / 100) * 100;
            return rounded.toString();
          }
        }
      },
      y: {
        type: 'linear',
        title: {
          display: true,
          text: 'Statischer Ventilatordruck P sf [Pa]'
        },
        min: minPsf,
        max: maxPsf,
        grid: {
          color: '#fefefe', // Very light gray grid
          borderDash: [3, 3]
        },
        ticks: {
          stepSize: 200,
          maxTicksLimit: Math.ceil((maxPsf - minPsf) / 200),
          callback: function(value) {
            // Round to nearest 100 and return as whole number (no decimals)
            const rounded = Math.round(value / 100) * 100;
            return rounded.toString();
          }
        }
      }
    },
    onClick: handleChartClick
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      padding: '20px', 
      gap: '20px', 
      fontFamily: 'Arial, sans-serif',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden',
      overflowY: 'visible',
      margin: 0
    }}>
      {/* Fan Image and Specifications Section */}
      {catalogSpecs && (
        <div style={{
          display: 'flex',
          gap: '20px',
          padding: '20px',
          backgroundColor: '#f9f9f9',
          borderRadius: '5px',
          border: '1px solid #ddd',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Fan Images Gallery */}
          <div style={{ flexShrink: 0 }}>
            {catalogSpecs.images && catalogSpecs.images.length > 0 ? (
              <div>
                <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '16px' }}>
                  Fan Images
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '10px',
                  maxWidth: '600px'
                }}>
                  {catalogSpecs.images.map((imgName, idx) => (
                    <div key={idx} style={{
                      border: '1px solid #ccc',
                      borderRadius: '5px',
                      padding: '5px',
                      backgroundColor: 'white',
                      textAlign: 'center'
                    }}>
                      <img 
                        src={`${process.env.PUBLIC_URL || ''}/images/${imgName}`}
                        alt={`${catalogSpecs.fan_model} - Image ${idx + 1}`}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          display: 'block',
                          margin: '0 auto'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const errorDiv = e.target.nextSibling;
                          if (errorDiv) {
                            errorDiv.style.display = 'block';
                          }
                        }}
                      />
                      <div style={{ 
                        display: 'none', 
                        fontSize: '11px', 
                        color: '#999',
                        padding: '10px'
                      }}>
                        Image not found
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{
                width: '300px',
                height: '300px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999'
              }}>
                No images available
              </div>
            )}
          </div>
          
          {/* Fan Specifications */}
          <div style={{ flex: '1' }}>
            <h2 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '20px' }}>
              {catalogSpecs.title}
            </h2>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                <strong>Typ:</strong> {catalogSpecs.fan_type}
              </div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                <strong>Größe:</strong> {catalogSpecs.fan_size}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                <strong>Modell:</strong> {catalogSpecs.fan_model}
              </div>
            </div>
            
            {/* Motor Options Table */}
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '16px' }}>
                Verfügbare Motoroptionen
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                  backgroundColor: 'white'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e0e0e0' }}>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ccc' }}>Leistung (kW)</th>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ccc' }}>Motor-Typ</th>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ccc' }}>Motor RPM</th>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ccc' }}>Ventilator RPM</th>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ccc' }}>Zubehör</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalogSpecs.motor_options.map((motor, index) => (
                      <tr key={index} style={{
                        backgroundColor: selectedMotor && selectedMotor.power_rating_kw === motor.power_kw ? '#fff4e6' : 'white'
                      }}>
                        <td style={{ padding: '8px', border: '1px solid #ccc' }}>{motor.power_kw}</td>
                        <td style={{ padding: '8px', border: '1px solid #ccc' }}>{motor.motor_type || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ccc' }}>{motor.motor_rpm || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ccc' }}>{motor.fan_rpm || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ccc', fontSize: '11px' }}>
                          {motor.accessories || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content Area */}
      <div style={{ 
        display: 'flex', 
        gap: '20px',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
      {/* Graph Area */}
      <div style={{ flex: '1', minWidth: '700px', flexShrink: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#333' }}>Fan Performance Graph (Chart.js)</h2>
          <button
            onClick={downloadLogs}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            title="Download debug logs"
          >
            Download Logs
          </button>
        </div>
        <div 
          style={{ 
            cursor: 'crosshair', 
            position: 'relative',
            height: '600px'
          }}
          title="Click on the graph to set a new operating point"
        >
          <Line
            ref={chartRef}
            data={chartData}
            options={chartOptions}
            key="fan-graph-chart" // Stable key to prevent unnecessary re-renders
            updateMode="none" // Don't auto-update, we'll manually update when needed
            onClick={(event, elements) => {
              console.log('Line component onClick triggered', { event, elements });
              handleChartClick(event, elements);
            }}
          />
        </div>
        <div style={{ marginTop: '5px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
          Click anywhere on the graph to set a new operating point
        </div>
        
        {/* Operating Point Labels */}
        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
          <div style={{ backgroundColor: '#003366', color: 'white', padding: '5px 10px', borderRadius: '3px' }}>
            qv: {operatingPoint.qv.toFixed(2)} m³/h
          </div>
          <div style={{ backgroundColor: '#003366', color: 'white', padding: '5px 10px', borderRadius: '3px' }}>
            psf: {operatingPoint.psf.toFixed(2)} Pa
          </div>
        </div>
        
        {/* Selected Motor Display */}
        {selectedMotor && (
          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            backgroundColor: '#e8f4f8', 
            border: '2px solid #0066cc',
            borderRadius: '5px'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#0066cc', fontSize: '16px' }}>
              Empfohlener Motor
            </h3>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              <div><strong>Leistung:</strong> {selectedMotor.power_rating_kw} kW</div>
              <div><strong>Ventilator-Drehzahl:</strong> {selectedMotor.fan_rpm.toFixed(0)} RPM</div>
              <div><strong>Motor-Drehzahl:</strong> {selectedMotor.motor_rpm?.toFixed(0) || 'N/A'} RPM</div>
              <div><strong>Motor-Typ:</strong> {selectedMotor.motor_type || 'N/A'}</div>
              <div><strong>Pole:</strong> {selectedMotor.poles}</div>
            </div>
          </div>
        )}
      </div>

      {/* Control Panel */}
      <div style={{ 
        width: '300px', 
        minWidth: '300px',
        flexShrink: 0,
        backgroundColor: '#f9f9f9', 
        padding: '20px', 
        borderRadius: '5px',
        display: 'block',
        visibility: 'visible',
        opacity: 1
      }}>
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '15px', color: '#333', fontSize: '16px' }}>BETRIEBSPUNKT ÄNDERN</h3>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Volumenstrom (m³/h):</label>
            <input
              type="number"
              value={operatingPoint.qv.toFixed(2)}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val) && val >= 0) {
                  setOperatingPoint({ ...operatingPoint, qv: val });
                }
              }}
              style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ccc', borderRadius: '3px' }}
            />
            <div style={{ fontSize: '11px', color: '#666', marginTop: '3px', fontStyle: 'italic' }}>
              Clicked: {operatingPoint.qv.toFixed(2)} m³/h
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Druck (Pa):</label>
            <input
              type="number"
              value={operatingPoint.psf.toFixed(2)}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val) && val >= 0) {
                  setOperatingPoint({ ...operatingPoint, psf: val });
                }
              }}
              style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ccc', borderRadius: '3px' }}
            />
            <div style={{ fontSize: '11px', color: '#666', marginTop: '3px', fontStyle: 'italic' }}>
              Clicked: {operatingPoint.psf.toFixed(2)} Pa
            </div>
          </div>
        </div>

        <div>
          <h3 style={{ marginBottom: '15px', color: '#333', fontSize: '16px' }}>ZUSÄTZLICHE KURVE EINBLENDEN</h3>
          <button
            onClick={() => setShowAdditionalCurves({
              ...showAdditionalCurves,
              power: !showAdditionalCurves.power
            })}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '5px',
              backgroundColor: showAdditionalCurves.power ? '#4CAF50' : '#f0f0f0',
              color: showAdditionalCurves.power ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            Elektrische Eingangsleistung
          </button>
          <button
            onClick={() => setShowAdditionalCurves({
              ...showAdditionalCurves,
              current: !showAdditionalCurves.current
            })}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '5px',
              backgroundColor: showAdditionalCurves.current ? '#4CAF50' : '#f0f0f0',
              color: showAdditionalCurves.current ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            Stromaufnahme
          </button>
          <button
            onClick={() => setShowAdditionalCurves({
              ...showAdditionalCurves,
              staticEfficiency: !showAdditionalCurves.staticEfficiency
            })}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '5px',
              backgroundColor: showAdditionalCurves.staticEfficiency ? '#4CAF50' : '#f0f0f0',
              color: showAdditionalCurves.staticEfficiency ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            Statischer Ventilatorwirkungsgrad
          </button>
          <button
            onClick={() => setShowAdditionalCurves({
              ...showAdditionalCurves,
              totalEfficiency: !showAdditionalCurves.totalEfficiency
            })}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '5px',
              backgroundColor: showAdditionalCurves.totalEfficiency ? '#4CAF50' : '#f0f0f0',
              color: showAdditionalCurves.totalEfficiency ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            Totaler Ventilatorwirkungsgrad
          </button>
          <button
            onClick={() => setShowAdditionalCurves({
              ...showAdditionalCurves,
              speed: !showAdditionalCurves.speed
            })}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '5px',
              backgroundColor: showAdditionalCurves.speed ? '#4CAF50' : '#f0f0f0',
              color: showAdditionalCurves.speed ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            Drehzahl
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default FanGraphChartJS;

